import React, { useState } from 'react';
import styled from 'styled-components';
import {
  AccessType,
  BuildJsonType,
  EnhancedAppointment,
} from '@bb/domain/dist/types';
import { Button } from 'components';
import SupraAccess from '@app/DashboardComponents/AppointmentDetailViewComponents/SupraAccess';
import { showingAgentInstructionsOptions } from '../../../../app/constants';
import Section from './Section';
import DetailRow from './DetailRow';
import LockboxPhotoModal from './LockboxPhotoModal';

const AccessDetailsInfo = ({
  appointment,
  lockboxPhoto,
}: {
  appointment: BuildJsonType<EnhancedAppointment>;
  lockboxPhoto?: string | null;
}) => {
  const [showModal, setShowModal] = useState(false);

  const openModal = () => setShowModal(true);

  const closeModal = () => setShowModal(false);
  const getAccessInfo = (access: AccessType) => {
    switch (access) {
      case 'Lock Box':
        return (
          <>
            Lockbox code:{' '}
            {appointment.appointmentConfig.instructions.lockbox?.code}
            <br />
            {lockboxPhoto && (
              <StyledButton
                type="link"
                onClick={openModal}
                data-cy="lockbox-photo-button"
              >
                View Lock box Photo
              </StyledButton>
            )}
            <br />
            {appointment.appointmentConfig.instructions.lockbox?.details}
          </>
        );
      case 'Direct':
        return (
          <>
            Direct
            <br />
            {appointment.appointmentConfig.instructions?.direct?.details}
          </>
        );
      case 'Door Code':
        return (
          <>
            Door Code:{' '}
            {appointment.appointmentConfig.instructions?.doorcode?.code}
            <br />
            {appointment.appointmentConfig.instructions?.doorcode?.details}
          </>
        );
      case 'Key':
        return (
          <>
            Key
            <br />
            {appointment.appointmentConfig.instructions?.key?.details}
          </>
        );
      case 'SentriLockV2':
        return (
          <>
            {appointment.dynamicLockBoxCode
              ? `SentriLock code: ${appointment.dynamicLockBoxCode}`
              : 'SentriLock'}
            <br />
            {lockboxPhoto && (
              <StyledButton
                type="link"
                onClick={openModal}
                data-cy="lockbox-photo-button"
              >
                View Lock box Photo
              </StyledButton>
            )}
            <br />
            {appointment.appointmentConfig.instructions.sentrilockv2?.details}
          </>
        );
      case 'Supra eKEY':
        return (
          <>
            <SupraAccess />
            <br />
            {lockboxPhoto && (
              <StyledButton
                type="link"
                onClick={openModal}
                data-cy="lockbox-photo-button"
              >
                View Lock box Photo
              </StyledButton>
            )}
            <br />
            {appointment.appointmentConfig.instructions.supraekey?.details &&
              `Special Access Instructions: ${appointment.appointmentConfig.instructions.supraekey?.details}`}
          </>
        );
      case 'Supra Lockbox':
        return (
          <>
            <SupraAccess isSupraLockbox />
            <br />
            {lockboxPhoto && (
              <StyledButton
                type="link"
                onClick={openModal}
                data-cy="lockbox-photo-button"
              >
                View Lock box Photo
              </StyledButton>
            )}
            <br />
            {appointment.appointmentConfig.instructions.supralockbox?.details &&
              `Special Access Instructions: ${appointment.appointmentConfig.instructions.supralockbox?.details}`}
          </>
        );
      case 'MasterLock':
        return (
          <>
            {appointment.dynamicLockBoxCode
              ? `MasterLock code: ${appointment.dynamicLockBoxCode}`
              : 'MasterLock'}
            <br />
            {lockboxPhoto && (
              <StyledButton
                type="link"
                onClick={openModal}
                data-cy="lockbox-photo-button"
              >
                View Lock box Photo
              </StyledButton>
            )}
            <br />
            {appointment.appointmentConfig.instructions.masterlock?.details}
          </>
        );

      default:
        <></>;
    }
  };

  if (appointment.status !== 'confirm' || !appointment?.appointmentConfig)
    return <></>;

  return (
    <>
      <Section title="Access">
        <DetailRow icon="lock" data-cy="access-info">
          {getAccessInfo(appointment.appointmentConfig.access)}
        </DetailRow>
        {!!(
          appointment.appointmentConfig.instructions.alarm?.code ||
          appointment.appointmentConfig.instructions.alarm?.details
        ) && (
          <DetailRow icon="lock" data-cy="alarm-code">
            <>
              Alarm Code:{' '}
              {appointment.appointmentConfig.instructions.alarm?.code}
              <br />
              {appointment.appointmentConfig.instructions.alarm?.details}
            </>
          </DetailRow>
        )}
      </Section>
      <Section title="Instructions">
        <DetailRow icon="book" data-cy="instructions-section">
          {showingAgentInstructionsOptions.map(({ value, label }) => (
              <StyledInstructionsItem key={value}>
                {appointment.appointmentConfig.instructions[value] === true &&
                  label}
              </StyledInstructionsItem>
            ))}
          {
            // Other Instructions
            appointment.appointmentConfig.instructions.confirmation
              ?.details && (
              <StyledInstructionsItem>
                {
                  appointment.appointmentConfig.instructions.confirmation
                    .details
                }
              </StyledInstructionsItem>
            )
          }
        </DetailRow>
      </Section>{' '}
      {lockboxPhoto && (
        <LockboxPhotoModal
          lockboxPhoto={lockboxPhoto}
          showModal={showModal}
          closeModal={closeModal}
        />
      )}
    </>
  );
};

export default AccessDetailsInfo;

const StyledInstructionsItem = styled.p`
  margin-bottom: 2px;
`;

const StyledButton = styled(Button)`
  margin: 0;
  padding: 0;
`;
